import i18nFactory from '@yandex-int/i18n';
import { uniq } from 'lodash';
import { Epic, ofType } from 'redux-observable';
import { EMPTY, forkJoin, from, Observable, of } from 'rxjs';
import { catchError, debounceTime, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { Action } from '../../actions';
import { changeLocationHard, updateRegions } from '../../actions/common';
import {
    PHRASES_SELECTOR_CLOSE_EDITOR,
    PHRASES_SELECTOR_GET_KEYWORDS,
    PHRASES_SELECTOR_GET_STATS,
    PHRASES_SELECTOR_MANUAL_VALUE_SUBMIT,
    PHRASES_SELECTOR_OPEN_EDITOR,
    PHRASES_SELECTOR_PHRASE_STATS_FETCH,
    PHRASES_SELECTOR_SAVE_PHRASES,
    PhrasesSelectorGetKeywordsAction,
    phrasesSelectorGetStats,
    PhrasesSelectorGetStatsAction,
    phrasesSelectorHideEditor,
    PhrasesSelectorHideEditorAction,
    PhrasesSelectorOpenEditorAction,
    PhrasesSelectorPhraseStatsFetchAction,
    phrasesSelectorResetStatus,
    PhrasesSelectorSavePhrasesAction,
    phrasesSelectorSavePhrasesError,
    phrasesSelectorSetGroupKeywords,
    phrasesSelectorSetLoadingFalse,
    phrasesSelectorSetStartPhrases,
    phrasesSelectorSetStartPhrasesError,
    phrasesSelectorShowEditor,
    phrasesSelectorUpdateStartPhrases
} from '../../actions/phrases-selector';
import {
    PHRASES_GENERATOR_GENERATE_PHRASES,
    PhrasesGeneratorGeneratePhrasesAction
} from '../../actions/phrases-selector/generator';
import {
    PHRASES_SELECTOR_ADD_MINUS_PHRASES,
    PHRASES_SELECTOR_CHANGE_MINUS_PHRASE,
    PHRASES_SELECTOR_CLEAR_MINUS_PHRASES,
    PHRASES_SELECTOR_REMOVE_MINUS_PHRASE,
    PhrasesSelectorAddMinusPhrasesAction,
    phrasesSelectorChangeManualValue,
    PhrasesSelectorChangeMinusPhraseAction,
    phrasesSelectorClearData,
    PhrasesSelectorClearMinusPhrasesAction,
    PhrasesSelectorRemoveMinusPhraseAction
} from '../../actions/phrases-selector/persistentData';
import {
    PHRASES_SELECTOR_CHANGE_REFINE_PHRASE,
    PHRASES_SELECTOR_GET_PHRASE_STATS,
    PHRASES_SELECTOR_GET_REFINE_WORDS,
    PhrasesSelectorChangeRefinePhraseAction,
    phrasesSelectorGetPhraseStats,
    PhrasesSelectorGetPhraseStatsAction,
    PhrasesSelectorGetRefineWordsAction,
    phrasesSelectorSetPhraseStats,
    phrasesSelectorSetRefinePhrasesError,
    phrasesSelectorSetRefinePhraseShowsError,
    phrasesSelectorSetRefineWords
} from '../../actions/phrases-selector/refinements';
import { GetPhrasesStatsResponsePhrase } from '../../api/RestApiManager';
import { mapRefineWords } from '../../mappers/phrasesSelectorWizard';
import { awaitCommonData } from '../../operators';
import {
    selectPhrasesSelectorData,
    selectPhrasesSelectorWizard
} from '../../selectors/phrasesSelector/phrasesSelector';
import { confirm } from '../../service/Confirm/Confirm';
import { ApiMethodExecutingError, NotSyncedError } from '../../service/Errors';
import { showNoticeMessage } from '../../service/GridHelpers/GridHelpers';
import { parseManualPhrases } from '../../service/PhrasesHelpers/PhrasesHelpers';
import {
    createPhrasesLink,
    filterPhrasesWithoutShows,
    generatePhrases,
    getValidationError,
    mapPhraseNestedStatsToClient,
    mapPhrasesStatsToClient
} from '../../service/PhrasesSelectorWizard/PhrasesSelectorWizard';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import '../../service/Rx';
import { EpicDeps } from '../../service/StoreFactory';
import { VirtualTreeHelpers } from '../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import { CampaignType } from '../../typings/domain';
import { DnaEpicAction } from '../../typings/epic';
import { RefinePhraseQuery } from '../../typings/graphql-grid';
import { NoticeType } from '../../typings/grid';
import { PhrasesSelectorWizardId } from '../../typings/phrases';
import { State } from '../../typings/state';

import * as keyset from './phrasesSelector.i18n';

const i18n = i18nFactory(keyset);

function getPhrasesStatsRequest(
    state: State,
    deps: EpicDeps,
    params: {
        wizardId: PhrasesSelectorWizardId;
        phrasesList: Array<string>;
    }
) {
    const { wizardId, phrasesList } = params;
    const [
        {
            groupInfo: { regionIds, minusKeywordsLibraryList, campaign, minusKeywords, id: groupId, groupType },
            startPhrases
        },
        { minusPhrases, isExcludePhrases }
    ] = selectPhrasesSelectorData(state, wizardId);

    const nodesStateMap = VirtualTreeHelpers.getNodesStateMap(startPhrases.nested, {
        expandable: {},
        expanded: {},
        loading: {},
        selected: {}
    });

    const newStartPhrasesNames = uniq([...startPhrases.nested.map(phrase => phrase.name), ...phrasesList]);
    const queryPhrases = uniq([...newStartPhrasesNames, ...Object.keys(nodesStateMap.expanded)]);
    const commonMinusPhrases = uniq([...minusKeywords, ...minusPhrases]);

    const request = deps.restApiManager.getPhrasesStats({
        campaignId: campaign.id,
        phrases: queryPhrases,
        commonMinusPhrases,
        libraryMinusPhrasesIds: minusKeywordsLibraryList,
        geo: regionIds,
        adGroupId: wizardId !== PhrasesSelectorWizardId.editGroupsPage ? groupId : undefined,
        adGroupType: groupType
    });

    return from(request).pipe(
        map((responsePhrases: Array<GetPhrasesStatsResponsePhrase>) => {
            const result = mapPhrasesStatsToClient(newStartPhrasesNames, queryPhrases, responsePhrases, nodesStateMap);

            if (isExcludePhrases) {
                return {
                    similar: filterPhrasesWithoutShows(result.similar),
                    nested: filterPhrasesWithoutShows(result.nested)
                };
            }

            return result;
        })
    );
}

export const generatePhrasesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(PHRASES_GENERATOR_GENERATE_PHRASES),
        map((action: PhrasesGeneratorGeneratePhrasesAction) => {
            const state = store$.value;

            const { wizardId } = action;
            const [
                ,
                {
                    generator: { groups }
                }
            ] = selectPhrasesSelectorData(state, wizardId);

            const generatedPhrases = generatePhrases(groups);

            return phrasesSelectorGetStats(wizardId, generatedPhrases);
        })
    );
};

export const manualInputSubmitEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_MANUAL_VALUE_SUBMIT),
        mergeMap((action: PhrasesGeneratorGeneratePhrasesAction) => {
            const state = store$.value;

            const { wizardId } = action;
            const [
                {
                    groupInfo: { id }
                },
                { manualValue }
            ] = selectPhrasesSelectorData(state, wizardId);
            const startPhrases = parseManualPhrases(manualValue);

            return of(phrasesSelectorGetStats(wizardId, startPhrases), phrasesSelectorChangeManualValue(id, ''));
        })
    );
};

export const getPhraseStatsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_PHRASE_STATS_FETCH),
        mergeMap(
            (action: PhrasesSelectorPhraseStatsFetchAction): Observable<Action> => {
                const { wizardId, phrase } = action;
                const state = store$.value;

                const [
                    {
                        groupInfo: { regionIds, id, minusKeywordsLibraryList, minusKeywords, campaign, groupType },
                        startPhrases: { nested: startNestedPhrases }
                    },
                    { minusPhrases, isExcludePhrases }
                ] = selectPhrasesSelectorData(state, wizardId);

                const commonMinusPhrases = uniq([...minusKeywords, ...minusPhrases]);
                const queryPhrases = [phrase.name];
                const request = deps.restApiManager.getPhrasesStats({
                    adGroupId: wizardId !== PhrasesSelectorWizardId.editGroupsPage ? id : undefined,
                    adGroupType: groupType,
                    campaignId: campaign.id,
                    phrases: queryPhrases,
                    commonMinusPhrases,
                    libraryMinusPhrasesIds: minusKeywordsLibraryList,
                    geo: regionIds
                });

                return from(request).pipe(
                    map((phrasesStats: Array<GetPhrasesStatsResponsePhrase>) => {
                        const phraseNested = mapPhraseNestedStatsToClient(
                            phrase,
                            phrasesStats,
                            startNestedPhrases,
                            isExcludePhrases
                        );

                        return phrasesSelectorUpdateStartPhrases(wizardId, phraseNested);
                    })
                );
            }
        )
    );
};

export const getRefineWords: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_GET_REFINE_WORDS),
        mergeMap(
            (action: PhrasesSelectorGetRefineWordsAction): Observable<Action> => {
                const state = store$.value;
                const { wizardId, phrase } = action;
                const [
                    {
                        groupInfo: { regionIds, groupType }
                    },
                    { minusPhrases }
                ] = selectPhrasesSelectorData(state, wizardId);

                return from(deps.phraseQueryProvider.refinePhrase(phrase, regionIds, minusPhrases, groupType)).pipe(
                    map(
                        (result: RefinePhraseQuery): Action => {
                            const { hasMore, words } = mapRefineWords(result);

                            return phrasesSelectorSetRefineWords(wizardId, words, hasMore);
                        }
                    ),
                    catchError(
                        (e: ApiMethodExecutingError): Observable<Action> => {
                            const message = e.getMessage() || i18n('refine-phrases-error');

                            return of(phrasesSelectorSetRefinePhrasesError(wizardId, message));
                        }
                    )
                );
            }
        )
    );
};

export const getRefinePhraseShowsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_GET_PHRASE_STATS),
        mergeMap(
            (action: PhrasesSelectorGetPhraseStatsAction): Observable<Action> => {
                const { wizardId, phrase } = action;
                const state = store$.value;
                const [
                    {
                        groupInfo: { regionIds, id, minusKeywords, minusKeywordsLibraryList, campaign, groupType }
                    },
                    { minusPhrases }
                ] = selectPhrasesSelectorData(state, wizardId);
                const commonMinusPhrases = uniq([...minusKeywords, ...minusPhrases]);

                return from(
                    deps.restApiManager.getPhrasesStats({
                        adGroupId: wizardId !== PhrasesSelectorWizardId.editGroupsPage ? id : undefined,
                        adGroupType: groupType,
                        campaignId: campaign.id,
                        phrases: [phrase],
                        commonMinusPhrases,
                        libraryMinusPhrasesIds: minusKeywordsLibraryList,
                        geo: regionIds
                    })
                ).pipe(
                    map(
                        (response: Array<GetPhrasesStatsResponsePhrase>): Action =>
                            phrasesSelectorSetPhraseStats(wizardId, response[0].shows)
                    ),
                    catchError(
                        (): Observable<Action> =>
                            of(phrasesSelectorSetRefinePhraseShowsError(wizardId, i18n('refine-phrases-shows-error')))
                    )
                );
            }
        )
    );
};

export const changeRefinePhraseEpic: Epic<Action, Action, State, EpicDeps> = action$ => {
    const REFINE_REQUEST_DELAY = 500;

    return action$.pipe(
        ofType(PHRASES_SELECTOR_CHANGE_REFINE_PHRASE),
        filter((action: PhrasesSelectorChangeRefinePhraseAction) => action.phrase !== ''),
        debounceTime(REFINE_REQUEST_DELAY),
        map((action: PhrasesSelectorChangeRefinePhraseAction) => {
            const { wizardId, phrase } = action;
            return phrasesSelectorGetPhraseStats(wizardId, phrase);
        })
    );
};

export const updatePhrasesStatsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(
            PHRASES_SELECTOR_REMOVE_MINUS_PHRASE,
            PHRASES_SELECTOR_ADD_MINUS_PHRASES,
            PHRASES_SELECTOR_CHANGE_MINUS_PHRASE,
            PHRASES_SELECTOR_CLEAR_MINUS_PHRASES
        ),
        mergeMap(
            (
                action:
                    | PhrasesSelectorRemoveMinusPhraseAction
                    | PhrasesSelectorAddMinusPhrasesAction
                    | PhrasesSelectorChangeMinusPhraseAction
                    | PhrasesSelectorClearMinusPhrasesAction
            ): Observable<Action> => {
                const { wizardId } = action;
                const state = store$.value;

                const {
                    startPhrases: { nested: nestedPhrases }
                } = selectPhrasesSelectorWizard(state, wizardId);

                if (nestedPhrases.length === 0) {
                    return EMPTY;
                }

                return of(phrasesSelectorGetStats(wizardId, nestedPhrases.map(p => p.name)));
            }
        )
    );
};

export const getPhrasesStatsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_GET_STATS),
        mergeMap(
            (action: PhrasesSelectorGetStatsAction): Observable<Action> => {
                const { wizardId, phrases: phrasesList } = action;
                const state = store$.value;

                return getPhrasesStatsRequest(state, deps, {
                    wizardId,
                    phrasesList
                }).pipe(
                    mergeMap(
                        (startPhrases): DnaEpicAction => [
                            phrasesSelectorSetStartPhrases(wizardId, startPhrases),
                            phrasesSelectorSetLoadingFalse(wizardId)
                        ]
                    ),
                    catchError(
                        (): Observable<Action> =>
                            of(phrasesSelectorSetStartPhrasesError(wizardId, i18n('start-phrases-error')))
                    )
                );
            }
        )
    );
};

export const openPhrasesSelectorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_OPEN_EDITOR),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: PhrasesSelectorOpenEditorAction): Observable<Action> => {
                const type = action.groupInfo.campaign.type;

                if (type === CampaignType.DYNAMIC) {
                    showNoticeMessage(NoticeType.error, i18n('no-phrases-for-dynamic-campaigns'));

                    return EMPTY;
                }

                if (type === CampaignType.PERFORMANCE) {
                    showNoticeMessage(NoticeType.error, i18n('no-phrases-for-smart-banners'));

                    return EMPTY;
                }

                return from(RegionsHelpers.fetchRegions()).pipe(
                    mergeMap(regionsResult => {
                        const actions: Array<Action> = [phrasesSelectorShowEditor(action.wizardId)];

                        if (!store$.value.commonData.regions) {
                            actions.push(updateRegions(regionsResult));
                        }

                        return actions;
                    })
                );
            }
        )
    );
};

export const getGroupKeywordsEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_GET_KEYWORDS),
        awaitCommonData(action$, store$),
        mergeMap(
            (action: PhrasesSelectorGetKeywordsAction): Observable<Action> => {
                const { wizardId, campaignId, groupId } = action;

                return from(
                    deps.groupsDataProvider.getGroupKeywordsList(store$.value.commonData.login, campaignId, groupId)
                ).pipe(
                    switchMap(
                        (keywordsResult): Observable<Action> => {
                            if (keywordsResult.length === 0) {
                                return of(phrasesSelectorResetStatus(wizardId));
                            }

                            return of(
                                phrasesSelectorSetGroupKeywords(wizardId, keywordsResult),
                                phrasesSelectorGetStats(wizardId, keywordsResult)
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> =>
                            of(phrasesSelectorSetStartPhrasesError(wizardId, i18n('start-phrases-error')))
                    )
                );
            }
        )
    );
};

export const savePhrasesEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_SAVE_PHRASES),
        mergeMap(
            (action: PhrasesSelectorSavePhrasesAction): Observable<Action> => {
                const { commonData } = store$.value;
                const [{ groupInfo }, { keywords, minusPhrases, priceRate }] = selectPhrasesSelectorData(
                    store$.value,
                    action.wizardId
                );
                const login = commonData.client.login;
                const adGroupId = groupInfo.id;

                if (!login) {
                    throw new Error('login is undefined');
                }
                const queryKeywords = keywords.map(keyword => ({
                    keyword: keyword.name,
                    adGroupId,
                    priceContext: priceRate ? String(priceRate) : undefined
                }));
                const queryMinusPhrases = minusPhrases;

                const addKeywordsPromise = queryKeywords.length
                    ? deps.phrasesMutationExecutor
                          .addKeywords(
                              {
                                  addItems: queryKeywords
                              },
                              login
                          )
                          .then(result => result.addKeywords.addedItems.map(k => k.keywordId))
                    : Promise.resolve([]);

                const addMinusKeywordsPromise = queryMinusPhrases.length
                    ? deps.phrasesMutationExecutor
                          .addAdGroupMinusKeywords(
                              {
                                  addItems: [
                                      {
                                          minusKeywords: queryMinusPhrases,
                                          adGroupId
                                      }
                                  ]
                              },
                              login
                          )
                          .then(result => result.addAdGroupMinusKeywords.addedItems)
                    : Promise.resolve([]);

                return forkJoin(addKeywordsPromise, addMinusKeywordsPromise).pipe(
                    mergeMap(
                        ([addedKeywords]): DnaEpicAction => {
                            const closePhrasesSelectorActions = [
                                phrasesSelectorClearData(adGroupId),
                                phrasesSelectorHideEditor(action.wizardId)
                            ];

                            if (addedKeywords.length === 0) {
                                return closePhrasesSelectorActions;
                            }

                            const link = createPhrasesLink({
                                login,
                                campaign: groupInfo.campaign,
                                groupId: adGroupId,
                                phrasesIds: addedKeywords
                            });

                            return [...closePhrasesSelectorActions, changeLocationHard(link)];
                        }
                    ),
                    catchError((err: ApiMethodExecutingError) => {
                        if (!(err instanceof NotSyncedError)) {
                            const validationErrors = err.getErrors().map(getValidationError);

                            showNoticeMessage(NoticeType.error, uniq(validationErrors).join(' '));
                        }

                        return of(phrasesSelectorSavePhrasesError(action.wizardId));
                    })
                );
            }
        )
    );
};

export const leavePhrasesSelectorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(PHRASES_SELECTOR_CLOSE_EDITOR),
        mergeMap(
            (action: PhrasesSelectorHideEditorAction): Observable<Action> => {
                return from(confirm({ message: i18n('changes-confirm') })).pipe(
                    mergeMap(
                        (): DnaEpicAction => {
                            const [
                                {
                                    groupInfo: { id }
                                }
                            ] = selectPhrasesSelectorData(store$.value, action.wizardId);

                            return [phrasesSelectorClearData(id), phrasesSelectorHideEditor(action.wizardId)];
                        }
                    ),
                    catchError((): Observable<Action> => EMPTY)
                );
            }
        )
    );
};
